import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@amzn/awsui-components-react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import {
  INLINE_SCHEME,
  InlineAttachmentImage,
} from "./attachments/inlineAttachments";
import { CodeProse, ReferenceIdChip, TransactionIdChip } from "./CodeProse";
import {
  isCodeReviewId,
  isMcmId,
  isSimTicketId,
  isTaskId,
  isTransactionId,
} from "./trackstarsText";
import { splitMentions } from "./mentions";
import { MentionChip } from "./MentionChip";
import "./CodeProse.scss";
import "./CommentMarkdown.scss";

/** One prose string: @mentions of `mentions` as chips, everything else through CodeProse. */
const MentionProse: React.FC<{
  text: string;
  mentions: ReadonlySet<string>;
}> = ({ text, mentions }) => {
  let offset = 0;
  return (
    <>
      {splitMentions(text, mentions).map((part) => {
        const key = offset;
        offset += typeof part === "string" ? part.length : part.text.length;
        return typeof part === "string" ? (
          <CodeProse key={key} text={part} />
        ) : (
          <MentionChip key={key} alias={part.alias} text={part.text} />
        );
      })}
    </>
  );
};

/**
 * Runs CodeProse over the plain-string children of a prose element, so identifiers the author did
 * not wrap in backticks (`guitar_tax_engine_internal_error_500`, `dlq_dynamo_job.py`,
 * `DcdCbeRecordGenerator.build()`) still read as code — the same treatment TrackStars
 * suggestions get. Element children (code, links, bold…) are left to their own renderers.
 * @mentions of the comment's validated users show as chips.
 */
const highlight = (
  children: React.ReactNode,
  mentions: ReadonlySet<string>
): React.ReactNode =>
  React.Children.map(children, (child) =>
    typeof child === "string" ? (
      <MentionProse text={child} mentions={mentions} />
    ) : (
      child
    )
  );

/** Aliases to highlight as @mentions in the comment being rendered. */
const MentionsContext = createContext<ReadonlySet<string>>(new Set());

type ProseProps = React.HTMLAttributes<HTMLElement> & {
  node?: unknown;
  children?: React.ReactNode;
};

const prose = (tag: string) => {
  const Prose = ({ node: _node, children, ...props }: ProseProps) => {
    const mentions = useContext(MentionsContext);
    return React.createElement(tag, props, highlight(children, mentions));
  };
  Prose.displayName = `Prose(${tag})`;
  return Prose;
};

const LUSCA_SEARCH_HOSTS = new Set(["lusca-search.prime-video.amazon.dev"]);

/**
 * The transaction a pasted Lusca Search link opens (`/?stage=Prod&transactionId=D01-…`), when the
 * link is a bare URL (its text is the URL itself) on Lusca Search — this site or the prod host.
 * Links with their own text (`[see this](…)`) are left as the author wrote them.
 */
export const pastedTransactionLink = (
  href: string | undefined,
  children: React.ReactNode
): { id: string; stage?: string } | null => {
  if (!href || typeof children !== "string" || children.trim() !== href)
    return null;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const local =
    typeof window !== "undefined" && url.origin === window.location.origin;
  if (!local && !LUSCA_SEARCH_HOSTS.has(url.hostname)) return null;
  if (url.pathname !== "/" && url.pathname !== "") return null;
  const id = url.searchParams.get("transactionId")?.trim();
  if (!id || !isTransactionId(id)) return null;
  return { id, stage: url.searchParams.get("stage") || undefined };
};

const COMPONENTS: Components = {
  a: ({ node: _node, children, ...props }) => {
    // A pasted Lusca Search transaction link reads as the order-ID chip (keeping the link's stage).
    const txn = pastedTransactionLink(props.href, children);
    if (txn)
      return <TransactionIdChip id={txn.id} stage={txn.stage} withIcon />;
    return (
      <a target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },
  // An order ID or AUID written in backticks still links to Lusca Search, and a CR, MCM, task or
  // ticket ID to its own page; other code renders as usual.
  code: ({ node: _node, children, className, ...props }) => {
    if (!className && typeof children === "string") {
      if (isTransactionId(children)) return <TransactionIdChip id={children} />;
      if (isCodeReviewId(children))
        return <ReferenceIdChip id={children} kind="codeReview" />;
      if (isMcmId(children))
        return <ReferenceIdChip id={children} kind="mcm" />;
      if (isTaskId(children))
        return <ReferenceIdChip id={children} kind="task" />;
      if (isSimTicketId(children))
        return <ReferenceIdChip id={children} kind="ticket" />;
    }
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  // `attachment:` images resolve to this comment's own attachments (see inlineAttachments).
  img: ({ node: _node, src, alt }) =>
    typeof src === "string" && src.startsWith(INLINE_SCHEME) ? (
      <InlineAttachmentImage
        attRef={src.slice(INLINE_SCHEME.length)}
        alt={alt}
      />
    ) : (
      <img className="comment-md__img" src={src} alt={alt} loading="lazy" />
    ),
  p: prose("p"),
  li: prose("li"),
  td: prose("td"),
  th: prose("th"),
  em: prose("em"),
  strong: prose("strong"),
  h1: prose("h1"),
  h2: prose("h2"),
  h3: prose("h3"),
};

/** Default sanitizing, plus the `attachment:` scheme for inline pasted images. */
const SANITIZE_SCHEMA = {
  ...defaultSchema,
  protocols: {
    ...defaultSchema.protocols,
    src: [...(defaultSchema.protocols?.src ?? []), "attachment"],
  },
};

const urlTransform = (url: string) =>
  url.startsWith(INLINE_SCHEME) ? url : defaultUrlTransform(url);

/**
 * Investigation comment body as GitHub-flavoured markdown (lists, tables, `code`,
 * fenced blocks, links), with code-looking tokens in prose highlighted. rehypeSanitize
 * strips raw HTML — comments are user and agent text, never trusted markup. Single
 * newlines are kept as line breaks (white-space: pre-line) because most comments are
 * written as plain text.
 */
export const CommentMarkdown: React.FC<{
  text: string;
  /** Aliases to highlight as @mentions (the comment's validated mentions). */
  mentions?: readonly string[];
}> = ({ text, mentions }) => {
  const known = useMemo(
    () => new Set((mentions ?? []).map((m) => m.toLowerCase())),
    [mentions]
  );
  return (
    <div className="comment-md" data-testid="comment-markdown">
      <MentionsContext.Provider value={known}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeSanitize, SANITIZE_SCHEMA]]}
          urlTransform={urlTransform}
          components={COMPONENTS}
        >
          {text}
        </ReactMarkdown>
      </MentionsContext.Provider>
    </div>
  );
};

export default CommentMarkdown;

/** Collapsed height of a long comment: roughly ten lines — a paragraph and a short list. */
export const COMMENT_CLAMP_PX = 240;

/**
 * A comment collapsed by its RENDERED height, not a character count: a short comment with a few
 * bullets shows in full, and only one that really runs past ~10 lines gets a fade and "Show more".
 * Expanded, it shows in full on the page (no inner scroll box).
 */
export const ClampedCommentMarkdown: React.FC<{
  text: string;
  mentions?: readonly string[];
}> = ({ text, mentions }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setOverflows(el.scrollHeight > COMMENT_CLAMP_PX + 24);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text]);

  const clamped = overflows && !expanded;
  return (
    <div>
      <div
        ref={ref}
        data-testid="detail-text"
        data-expanded={expanded ? "true" : "false"}
        className={
          clamped ? "comment-clamp comment-clamp--collapsed" : "comment-clamp"
        }
        style={clamped ? { maxHeight: COMMENT_CLAMP_PX } : undefined}
      >
        <CommentMarkdown text={text} mentions={mentions} />
      </div>
      {overflows && (
        <Button variant="inline-link" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </div>
  );
};
